import { useCallback, useState } from 'react';
import type { OrderStatus } from '../../shared/orderStatus';
import { ApiCallError, fetchOrderStatus } from '../api/client';

interface UseOrderStatusReturn {
  order: OrderStatus | null;
  loading: boolean;
  error: string | null;
  errorStatus: number | null;
  lastSystemId: string;
  fetch: (systemId: string) => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
}

export function useOrderStatus(): UseOrderStatusReturn {
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [lastSystemId, setLastSystemId] = useState('');

  const fetchById = useCallback(async (systemId: string) => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    setLastSystemId(systemId);
    try {
      const data = await fetchOrderStatus(systemId);
      setOrder(data);
    } catch (err) {
      setOrder(null);
      if (err instanceof ApiCallError) {
        setError(err.message);
        setErrorStatus(err.status);
      } else {
        setError(err instanceof Error ? err.message : 'Unexpected error.');
        setErrorStatus(0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (lastSystemId) await fetchById(lastSystemId);
  }, [lastSystemId, fetchById]);

  const clear = useCallback(() => {
    setOrder(null);
    setError(null);
    setErrorStatus(null);
    setLastSystemId('');
  }, []);

  return {
    order,
    loading,
    error,
    errorStatus,
    lastSystemId,
    fetch: fetchById,
    refresh,
    clear,
  };
}
